/**
 * CountryMultiPicker — searchable multi-select for ISO-3166-1 alpha-2 country
 * codes. Used by the AI Campaign Wizard so non-IT users don't have to type
 * "AE, SA" by hand.
 *
 * The list is the ~85 most-commonly-targeted markets (MENA-heavy because the
 * CRM's primary user base is in the Gulf). Not exhaustive on purpose — keeps
 * the picker scrollable. If a country isn't in the list, users can still type
 * its ISO-2 code into the search box and press Enter to add it.
 */
import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface Country {
  code: string;
  name: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  // MENA / GCC — top of list because user base
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩' },
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷' },

  // South Asia
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵' },

  // SE Asia + East Asia
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },

  // Europe — top markets
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴' },
  { code: 'CZ', name: 'Czechia', flag: '🇨🇿' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },

  // Americas
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' },

  // Oceania
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },

  // Africa (sub-Saharan key markets)
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
];

const BY_CODE = new Map(COUNTRIES.map(c => [c.code, c]));
const ISO2_RE = /^[A-Z]{2}$/;

interface Props {
  value: string[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const CountryMultiPicker: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Pick countries…',
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  const toggle = (code: string) => {
    if (value.includes(code)) onChange(value.filter(x => x !== code));
    else onChange([...value, code]);
  };

  const remove = (code: string) => onChange(value.filter(x => x !== code));

  // Allow typing a 2-letter code and pressing Enter to add a country that's
  // not in our curated list (e.g. AD, MT, IS).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const code = query.trim().toUpperCase();
    if (!ISO2_RE.test(code)) return;
    e.preventDefault();
    if (!value.includes(code)) onChange([...value, code]);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger / chips area */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={clsx(
          'flex w-full items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm transition-colors',
          'hover:border-border-strong focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        <div className="flex flex-1 flex-wrap gap-1 min-h-[24px] items-center">
          {value.length === 0 ? (
            <span className="text-text-muted text-xs">{placeholder}</span>
          ) : (
            value.map(code => {
              const c = BY_CODE.get(code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-1.5 py-0.5 text-xs font-medium text-primary"
                >
                  <span>{c?.flag ?? '🏳️'}</span>
                  <span>{code}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={(e) => { e.stopPropagation(); remove(code); }}
                    className="hover:opacity-70"
                    aria-label={`Remove ${code}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              );
            })
          )}
        </div>
        <ChevronDown size={14} className={clsx('shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-40 mt-1 rounded-xl border border-border bg-surface-elevated shadow-elevated overflow-hidden">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search size={13} className="shrink-0 text-text-muted" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by name or ISO code (e.g. UAE or AE)"
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-xs text-text-muted">
                  Not in our shortlist? Type the 2-letter ISO code and press Enter to add it.
                </div>
              ) : (
                filtered.map(c => {
                  const selected = value.includes(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => toggle(c.code)}
                      className={clsx(
                        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors',
                        selected ? 'bg-primary-soft' : 'hover:bg-surface-alt',
                      )}
                    >
                      <span className="text-base">{c.flag}</span>
                      <span className="flex-1 truncate text-sm text-text-primary">{c.name}</span>
                      <span className="font-mono text-2xs text-text-muted">{c.code}</span>
                      {selected && <Check size={13} className="shrink-0 text-primary" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
